import axios, { isAxiosError } from 'axios';

import { CONFIG } from 'constants/config';
import { stall } from 'helpers/utils';

import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource
} from 'axios';

import type {
  HttpClient, HttpClientConfig, HttpClientError, HttpInterceptorId, HttpRequestInterceptor, HttpResponseErrorInterceptor, HttpResponseInterceptor
} from 'types';

const defaultOptions: HttpClientConfig = {
  timeout: CONFIG.DURATIONS.DEFAULT_REQUEST_TIMEOUT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  },
  retries: 0,
  retryDelay: 500
};

export const isHttpClientError = isAxiosError;

export function createHttpClient(config: HttpClientConfig = {}): HttpClient {

  const axiosConfig: HttpClientConfig = {
    ...defaultOptions,
    ...config,
    headers: {
      ...defaultOptions.headers,
      ...(config?.headers ? config.headers : {})
    }
  };

  const { retries = 0, retryDelay = 500 } = axiosConfig;
  const instance: AxiosInstance = axios.create(axiosConfig);

  /* Interceptors */
  let cancelTokenSource: CancelTokenSource | null = null;
  let requestInterceptors: HttpInterceptorId[] = [];
  let responseInterceptors: HttpInterceptorId[] = [];

  /* Tokens */
  let accessToken = '';
  let refreshToken = '';
  let previewToken = '';

  const getAccessToken = () => accessToken;
  const setAccessToken = (at: string) => accessToken = at;

  const getRefreshToken = () => refreshToken;
  const setRefreshToken = (rt: string) => refreshToken = rt;

  const getPreviewToken = () => previewToken;
  const setPreviewToken = (pt: string) => previewToken = pt;

  const addRequestInterceptor = (interceptor: HttpRequestInterceptor) => {
    // @ts-ignore TODO: fix type
    const id = instance.interceptors.request.use((requestConfig: any) => {
      const modifiedConfig: AxiosRequestConfig = interceptor(requestConfig);
      return { ...axiosConfig, ...modifiedConfig };
    });
    requestInterceptors.push(id);
    return id;
  };

  const addResponseInterceptor = <R = any, E = HttpClientError>(
    interceptor: HttpResponseInterceptor<R>,
    errorInterceptor?: HttpResponseErrorInterceptor<E>
  ) => {
    const id = instance.interceptors.response.use(
      interceptor,
      errorInterceptor || ((error) => Promise.reject(error))
    );
    return id;
  };

  const removeInterceptor = (type: 'request' | 'response', id: HttpInterceptorId) => {
    instance.interceptors[type].eject(id);
    if (type === 'request') {
      requestInterceptors = requestInterceptors.filter((i) => i !== id);
    } else {
      responseInterceptors = responseInterceptors.filter((i) => i !== id);
    }
  };

  const removeInterceptors = () => {
    removeRequestInterceptors();
    removeResponseInterceptors();
  };

  const removeRequestInterceptors = () => {
    instance.interceptors.request.clear();
    requestInterceptors = [];
  };

  const removeResponseInterceptors = () => {
    instance.interceptors.response.clear();
    responseInterceptors = [];
  };

  const requestWithRetries = async <R = any>(
    requestConfig: AxiosRequestConfig,
    _retries: number = 0,
    _delay: number = 500
  ): Promise<AxiosResponse<R>> => {
    try {
      cancelTokenSource = axios.CancelToken.source();
      requestConfig.cancelToken = cancelTokenSource.token;
      return await instance(requestConfig);
    } catch (error) {
      if (_retries) {
        await stall(_delay);
        return requestWithRetries(requestConfig, retries - 1, _delay * 2);
      }
      throw error;
    }
  };

  const interceptors = () => ({
    request: [...requestInterceptors],
    response: [...responseInterceptors],
  });

  return {
    get: <R = any>(url: string, requestConfig?: HttpClientConfig) => requestWithRetries<R>({ ...requestConfig, url, method: 'GET' }, retries, retryDelay),
    post: <R = any>(url: string, data?: any, requestConfig?: HttpClientConfig) => requestWithRetries<R>({
      ...requestConfig, url, data, method: 'POST'
    }, retries, retryDelay),
    put: <R = any>(url: string, data?: any, requestConfig?: HttpClientConfig) => requestWithRetries<R>({
      ...requestConfig, url, data, method: 'PUT'
    }, retries, retryDelay),
    patch: <R = any>(url: string, data?: any, requestConfig?: HttpClientConfig) => requestWithRetries<R>({
      ...requestConfig, url, data, method: 'PATCH'
    }, retries, retryDelay),
    mergePatch: <R = any>(url: string, data?: any, requestConfig?: HttpClientConfig) => requestWithRetries<R>({
      ...requestConfig,
      url,
      data,
      method: 'PATCH',
      headers: {
        ...(requestConfig?.headers ? requestConfig.headers : {}),
        'Content-Type': 'application/merge-patch+json',
      }
    }, retries, retryDelay),
    delete: <R = any>(url: string, requestConfig?: HttpClientConfig) => requestWithRetries<R>({ ...requestConfig, url, method: 'DELETE' }, retries, retryDelay),
    cancelRequest: () => cancelTokenSource?.cancel(),
    addRequestInterceptor,
    addResponseInterceptor,
    removeInterceptor,
    removeInterceptors,
    removeRequestInterceptors,
    removeResponseInterceptors,
    interceptors,
    getInstance: () => instance,
    getAccessToken,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    getPreviewToken,
    setPreviewToken
  };
}
